import * as THREE from 'three';
import type { FloorLayoutModule } from '../../floor/dynamic-floor.js';
import type { TableLayoutOffset } from '../../editor/layout-types.js';
import { INTERACTIVE_USER_DATA_KEY } from '../types.js';
import { createPremiumTableGroup } from './table-furniture.js';

interface AnimTarget {
  group: THREE.Group;
  baseX: number;
  baseZ: number;
  tx: number;
  tz: number;
  ty: number;
  rotY: number;
}

export class DynamicTableLayer {
  readonly root = new THREE.Group();
  private targets = new Map<string, AnimTarget>();
  readonly tableTops = new Map<string, THREE.Mesh>();
  private editorOffsets = new Map<string, TableLayoutOffset>();

  applyEditorOffsets(offsets: TableLayoutOffset[]): void {
    this.editorOffsets = new Map(offsets.map((o) => [o.moduleId, o]));
    for (const [id, anim] of this.targets) {
      const off = this.editorOffsets.get(id);
      if (anim.group.userData.dragPreview) continue;
      anim.tx = anim.baseX + (off?.dx ?? 0);
      anim.tz = anim.baseZ + (off?.dz ?? 0);
      anim.rotY = off?.rotationY ?? 0;
    }
  }

  setDragPreview(moduleId: string, worldX: number | null, worldZ: number | null): void {
    const anim = this.targets.get(moduleId);
    if (!anim) return;
    if (worldX == null || worldZ == null) {
      anim.group.userData.dragPreview = false;
      const off = this.editorOffsets.get(moduleId);
      anim.tx = anim.baseX + (off?.dx ?? 0);
      anim.tz = anim.baseZ + (off?.dz ?? 0);
      return;
    }
    anim.group.userData.dragPreview = true;
    anim.tx = worldX;
    anim.tz = worldZ;
  }

  pickModuleId(object: THREE.Object3D | null): string | null {
    let cur: THREE.Object3D | null = object;
    while (cur) {
      if (typeof cur.userData.moduleId === 'string') return cur.userData.moduleId;
      cur = cur.parent;
    }
    return null;
  }

  getGroups(): THREE.Group[] {
    return [...this.targets.values()].map((t) => t.group);
  }

  syncLayout(layout: FloorLayoutModule[]): void {
    const seen = new Set<string>();
    for (const m of layout) {
      seen.add(m.moduleId);
      let anim = this.targets.get(m.moduleId);
      if (!anim) {
        const group = createPremiumTableGroup(m.logicalTableId, m.kind, m.seatCapacity, m.occupied);
        group.scale.setScalar(0.92);
        group.userData.moduleId = m.moduleId;
        group.userData.logicalTableId = m.logicalTableId;
        this.root.add(group);
        group.traverse((c) => {
          if (c.userData[INTERACTIVE_USER_DATA_KEY]) {
            this.tableTops.set(String(m.logicalTableId), c as THREE.Mesh);
          }
        });
        anim = { group, baseX: m.worldX, baseZ: m.worldZ, tx: m.worldX, tz: m.worldZ, ty: 0, rotY: 0 };
        this.targets.set(m.moduleId, anim);
      }
      anim.baseX = m.worldX;
      anim.baseZ = m.worldZ;
      const off = this.editorOffsets.get(m.moduleId);
      anim.tx = m.worldX + (off?.dx ?? 0);
      anim.tz = m.worldZ + (off?.dz ?? 0);
      anim.rotY = off?.rotationY ?? 0;
      const scale = m.occupied ? 1 : 0.96;
      anim.group.userData.targetScale = scale;
    }
    for (const [id, anim] of this.targets) {
      if (!seen.has(id)) {
        anim.ty = -2;
        anim.group.userData.fadeOut = true;
      }
    }
  }

  tick(dt: number): void {
    for (const [id, anim] of this.targets) {
      const g = anim.group;
      g.position.x = THREE.MathUtils.lerp(g.position.x, anim.tx, 1 - Math.pow(0.001, dt));
      g.position.z = THREE.MathUtils.lerp(g.position.z, anim.tz, 1 - Math.pow(0.001, dt));
      g.position.y = THREE.MathUtils.lerp(g.position.y, anim.ty, 1 - Math.pow(0.001, dt));
      g.rotation.y = THREE.MathUtils.lerp(g.rotation.y, anim.rotY, 1 - Math.pow(0.001, dt));
      const ts = (g.userData.targetScale as number) ?? 1;
      const s = THREE.MathUtils.lerp(g.scale.x, ts, 1 - Math.pow(0.001, dt));
      g.scale.setScalar(s);
      if (g.userData.fadeOut && g.position.y < -1.5) {
        this.root.remove(g);
        this.targets.delete(id);
      }
    }
  }

  getInteractives(): THREE.Object3D[] {
    return [...this.targets.values()].map((t) => t.group);
  }
}
